/***********************************
 *  Grade6MathLesson.js  (localStorage user + range-safe)
 *  本地 user + avatar，完整同步 G3/G4 設計，防 NaN
 ***********************************/
import React, {
  useState, useEffect, useRef,
  useCallback, useMemo
} from "react";
import "./Grade6MathLesson.css";

/* 3rd-party */
import * as d3 from "d3";

/* shared UI / data */
import Calculator from "../MathTools/Calculator";
import WritingPad from "../MathTools/WritingPad";
import LessonUI   from "../MathPagesStructure/LessonUI";
import ProgressBar from "../MathPagesStructure/ProgressBar";
import {
  StoryModeQuiz, QuizResultScreen, FinalQuizUI
} from "../MathPagesStructure/StoryModeStructure";
import { mathChapterPages } from "../MathLessonPools";
import { generateQuizQuestion } from "./Grade6QuizGenerator";

/* helpers */
const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
const chapterTopicMap = {
  1: "ratios",
  2: "algebra",
  3: "integers",
  4: "decimals",
  5: "geometry",
  6: null
};

export default function Grade6MathLesson({ onAwardTokens }) {
  // 1. 用 localStorage 取 user
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("loggedInUser");
    return u ? JSON.parse(u) : null;
  });

  // 2. 章節與頁面進度
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  // 3. 小節與總結進度
  const [showChQuiz, setShowChQuiz] = useState(false);
  const [chQuiz, setChQuiz] = useState([]);
  const [chIdx, setChIdx] = useState(0);
  const [chScore, setChScore] = useState(0);
  const [chDone, setChDone] = useState(false);

  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fDone, setFDone] = useState(false);
  const [time, setTime] = useState(480);

  // 完成旗標
  const [allDone, setAllDone] = useState(false);
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

  // 工具
  const [showCalc, setShowCalc] = useState(false);
  const [showPad, setShowPad] = useState(false);

  // Ratio/Algebra/Integers demo
  const [ratioA, setRatioA] = useState(3);
  const [ratioB, setRatioB] = useState(2);
  const ratioRef = useRef(null);

  const [aA, setAA] = useState(2);
  const [aB, setAB] = useState(3);
  const [aC, setAC] = useState(9);
  const [userX, setUserX] = useState("");
  const [xCorrect, setXCorrect] = useState(null);
  const correctX = useMemo(() => (aC - aB) / aA, [aA, aB, aC]);
  const newAlgebra = () => {
    setAA(Math.floor(Math.random()*5)+1);
    setAB(Math.floor(Math.random()*11)-5);
    setAC(Math.floor(Math.random()*10)+2);
    setUserX(""); setXCorrect(null);
  };

  const [intA, setIntA] = useState(-3);
  const [intB, setIntB] = useState(5);
  const intRef = useRef(null);

  // 頭像（跟 G3/G4 完全一樣）
  const avatar =
    user?.avatar ||
    user?.photoURL ||
    `${process.env.PUBLIC_URL}/images/Student.png`;

  const quizUser = {
    ...user,
    avatar,
    profilePic: avatar,
  };

  // ========== 已完成紀錄（localStorage 判斷，不走 Firebase）==========
  useEffect(() => {
    const done = localStorage.getItem("grade6MathComplete");
    if (done === "true") {
      setHasCompletedBefore(true);
      setAllDone(true);
    }
  }, []);
  useEffect(() => {
    if (allDone) localStorage.setItem("grade6MathComplete", "true");
  }, [allDone]);

  // ========== Ratio & Integer D3 Demo ==========
  const drawRatio = useCallback(() => {
    if (!ratioRef.current) return;
    d3.select(ratioRef.current).selectAll("*").remove();
    const unit = 300 / (ratioA + ratioB);
    const svg  = d3.select(ratioRef.current)
                   .append("svg")
                   .attr("width", 340)
                   .attr("height", 60);

    svg.append("rect")
       .attr("x", 20).attr("y", 10)
       .attr("width", ratioA * unit).attr("height", 30)
       .attr("fill", "steelblue");

    svg.append("rect")
       .attr("x", 20 + ratioA * unit).attr("y", 10)
       .attr("width", ratioB * unit).attr("height", 30)
       .attr("fill", "#ff7272");
  }, [ratioA, ratioB]);
  const drawIntLine = useCallback(() => {
    if (!intRef.current) return;
    d3.select(intRef.current).selectAll("*").remove();
    const svg = d3.select(intRef.current)
                  .append("svg")
                  .attr("width", 400)
                  .attr("height", 70);

    svg.append("line")
       .attr("x1", 10).attr("y1", 35)
       .attr("x2", 390).attr("y2", 35)
       .attr("stroke", "#000");

    for (let i=-10;i<=10;i++){
      const x=200+i*18;
      svg.append("line").attr("x1",x).attr("y1",30).attr("x2",x).attr("y2",40);
      svg.append("text").attr("x",x-4).attr("y",55).style("fontSize",10).text(i);
    }
    svg.append("circle")
       .attr("cx",200+(intA+intB)*18)
       .attr("cy",35).attr("r",5).attr("fill","red");
  }, [intA, intB]);
  useEffect(() => {
    const cp = mathChapterPages[6]?.[chapter]?.[pageIdx];
    if (cp?.isRatioInteractive)   drawRatio();
    if (cp?.isIntegerInteractive) drawIntLine();
  }, [chapter, pageIdx, drawRatio, drawIntLine]);

  // ========== Range Safe Pages ==========
  const pages = mathChapterPages[6]?.[chapter] || [];
  const currentPage = pages[pageIdx];

  // ========== 小節 Quiz ==========
  const startChapterQuiz = () => {
    const topic = chapterTopicMap[chapter];
    if (!topic) { setAllDone(true); return; }
    setChQuiz(Array.from({length:8}, () => generateQuizQuestion(topic)));
    setChIdx(0); setChScore(0); setChDone(false); setShowChQuiz(true);
  };
  const handleChAnswer = (opt) => {
    if (String(opt) === String(chQuiz[chIdx].correctAnswer)) setChScore(s=>s+1);
    chIdx < chQuiz.length-1 ? setChIdx(i=>i+1) : setChDone(true);
  };
  const finishChapterQuiz = () => {
    if (chScore >= 4) {
      if (chapter < 6) { setChapter(c=>c+1); setPageIdx(0); }
      else             { setAllDone(true); }
    }
    setShowChQuiz(false);
  };

  // ========== Final Quiz ==========
  const [fAwarded, setFAwarded] = useState(false);
  const startFinalQuiz = () => {
    const used = new Set();
    const arr  = [];
    while (arr.length < 50) {
      const q = generateQuizQuestion();
      const k = q.question + "|" + q.correctAnswer;
      if (!used.has(k)) { used.add(k); arr.push(q); }
    }
    setFinalQs(arr);
    setFIdx(0); setFScore(0); setFDone(false); setTime(480); setShowFinal(true);
    setFAwarded(false);
  };
  const handleFinalAns = (opt) => {
    if (String(opt) === String(finalQs[fIdx].correctAnswer)) setFScore(s=>s+1);
    fIdx < finalQs.length-1 ? setFIdx(i=>i+1) : setFDone(true);
  };
  useEffect(() => {
    if (!showFinal || fDone) return;
    if (time === 0) { setFDone(true); return; }
    const t = setTimeout(()=>setTime(t=>t-1),1000);
    return ()=>clearTimeout(t);
  }, [showFinal, time, fDone]);
  // Award coins (本地更新/可接同步API)
  useEffect(() => {
    if (!fDone || fAwarded) return;
    if ((fScore / finalQs.length) * 100 >= 80) {
      if (onAwardTokens) onAwardTokens(1);
      setFAwarded(true);
    }
  }, [fDone, fScore, finalQs.length, onAwardTokens, fAwarded]);

  // ========== Progress Bar 安全 ==========
  const totalSlides = useMemo(
    () => Object.values(mathChapterPages[6] || {})
                .reduce((acc,arr)=>acc+(arr?.length||0),0),
    []
  );
  const slidesSoFar = useMemo(() => {
    let sum=0;
    for(let c=1;c<chapter;c++) sum+=mathChapterPages[6]?.[c]?.length || 0;
    return sum+pageIdx;
  },[chapter,pageIdx]);
  const progress = totalSlides ? Math.round(((slidesSoFar+1)/totalSlides)*100) : 0;

  // ========== Extra (互動 demo) ==========
  const renderExtras = ({ page }) => {
    if (page.isRatioInteractive) {
      const simp = `${ratioA/gcd(ratioA,ratioB)}:${ratioB/gcd(ratioA,ratioB)}`;
      return (
        <div style={{marginTop:12}}>
          <label>Blue </label>
          <input type="number" value={ratioA}
                 onChange={e=>setRatioA(+e.target.value||1)}
                 style={{width:60,marginRight:10}}/>
          <label>Red </label>
          <input type="number" value={ratioB}
                 onChange={e=>setRatioB(+e.target.value||1)}
                 style={{width:60}}/>
          <p>Ratio {ratioA}:{ratioB} (simplified {simp})</p>
          <div ref={ratioRef}/>
        </div>
      );
    }
    if (page.isAlgebraInteractive) {
      return (
        <div style={{marginTop:12,border:"1px solid #ccc",padding:10}}>
          <p>Solve: {`${aA}x ${aB<0?"- "+Math.abs(aB):"+ "+aB} = ${aC}`}</p>
          <input type="number" value={userX}
                 onChange={e=>setUserX(e.target.value)}
                 style={{width:80}}/>
          <button className="button1"
                  onClick={()=>setXCorrect(Math.abs(+userX-correctX)<0.01)}>
            Check
          </button>
          <button className="button1" style={{marginLeft:8}} onClick={newAlgebra}>
            New
          </button>
          {xCorrect===true && <span style={{color:"green",marginLeft:10}}>✓ Correct</span>}
          {xCorrect===false&& <span style={{color:"red",marginLeft:10}}>✗ Try again</span>}
        </div>
      );
    }
    if (page.isIntegerInteractive) {
      return (
        <div style={{marginTop:12}}>
          <input type="number" value={intA}
                 onChange={e=>setIntA(+e.target.value||0)}
                 style={{width:60,marginRight:8}}/>
          <input type="number" value={intB}
                 onChange={e=>setIntB(+e.target.value||0)}
                 style={{width:60}}/>
          <p>Sum: {intA+intB}</p>
          <div ref={intRef}/>
        </div>
      );
    }
    return null;
  };

  // ========== 小節 Quiz Fragment ==========
  const ChapterQuizFragment = !chDone ? (
    <StoryModeQuiz
      questions     ={chQuiz}
      currentIndex  ={chIdx}
      score         ={chScore}
      onAnswer      ={handleChAnswer}
      user          ={quizUser}
      teacherAvatar="/images/Teacher.png"
      extraTools={
        <>
       <button onClick={() => setShowCalc(c => !c)}>
        {showCalc ? "Hide Calculator" : "Show Calculator"}
        </button>
        <button onClick={() => setShowPad(p => !p)}>
        {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
        </button>
        {showCalc && <Calculator />}
        {showPad  && <WritingPad />}
        </>
        }
    />
  ) : (
    <QuizResultScreen
      score={chScore}
      total={chQuiz.length}
      onContinue={finishChapterQuiz}
    />
  );

  // ========== Final Quiz Fragment ==========
  const FinalQuizFragment = (
    <FinalQuizUI
      questions    ={finalQs}
      currentIndex ={fIdx}
      score        ={fScore}
      timeLeft     ={time}
      onAnswer     ={handleFinalAns}
      quizFinished ={fDone}
      user         ={quizUser}
      onReturn     ={()=>setShowFinal(false)}
      teacherAvatar="/images/Teacher.png"
      resultMsg={
        fDone && (
          <p>You now have <strong>{user?.coins ?? 0}</strong> coins!</p>
        )
      }
    >
    <div style={{ marginTop: 16 }}>
      <button onClick={() => setShowCalc(c => !c)}>
        {showCalc ? "Hide Calculator" : "Show Calculator"}
      </button>
      <button onClick={() => setShowPad(p => !p)}>
        {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
      </button>
      {showCalc && <Calculator />}
      {showPad  && <WritingPad />}
    </div>
    </FinalQuizUI>
  );

  // ========== Render ==========
  return (
    <div className="grade6-math-page">
      <div className="grade6-math-page-scale">
        <ProgressBar progress={progress}/>

        {hasCompletedBefore && !showFinal && !showChQuiz && (
          <div className="jump-ui">
            <p>You previously completed Grade 6. Jump anywhere or re-take the final:</p>
            {[1,2,3,4,5].map(c=>(
              <button key={c} onClick={()=>{setChapter(c);setPageIdx(0);}}>
                Chapter {c}
              </button>
            ))}
            <button onClick={startFinalQuiz}>Final Quiz</button>
          </div>
        )}

        {/* slides OR chapter quiz */}
        {!showChQuiz && !showFinal && (
          <LessonUI
            pages={pages}
            currentPageIndex={pageIdx}
            onPrevPage={()=>setPageIdx(p=>p-1)}
            onNextPage={()=>setPageIdx(p=>p+1)}
            onStartQuiz={startChapterQuiz}
            backTo="/math"
            renderExtraSection={renderExtras}
          />
        )}
        {showChQuiz && ChapterQuizFragment}

        {/* final-quiz entry */}
        {allDone && !showFinal && !showChQuiz && (
          <div style={{textAlign:"center"}}>
            <h3>All Chapters Complete!</h3>
            <button className="lesson-nav-btn" onClick={startFinalQuiz}>
              Start Final Quiz
            </button>
          </div>
        )}

        {/* final quiz */}
        {showFinal && FinalQuizFragment}
      </div>
    </div>
  );
}
